import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import classNames from "classnames";
import { makeStyles } from "@material-ui/core/styles";
import Tooltip from "@material-ui/core/Tooltip";
import { useHistory, Link } from "react-router-dom";

import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import TextField from "@material-ui/core/TextField";
import IconButton from "@material-ui/core/IconButton";
import CircularProgress from "@material-ui/core/CircularProgress";

import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Parallax from "components/Parallax/Parallax.js";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import Button from "components/CustomButtons/Button.js";

import { localApi, credentials } from "utils/headApi";

import productStyle from "assets/jss/material-kit-pro-react/views/productStyle.js";
import aboutUsStyle from "assets/jss/material-kit-pro-react/views/blogPostsPageStyle.js";

import InshaHeader from "views/InshaBanglesPage/Section/InshaHeader";
import InshaHeaderLink from "views/InshaBanglesPage/Section/InshaHeaderLink";
import Footer from "views/InshaBanglesPage/Section/InshaFooter";

import {
  Favorite,
  FavoriteBorder,
  ChatBubbleOutline,
  Repeat,
  ShareOutlined,
  Phone,
  WhatsApp,
  Bookmark,
  BookmarkBorder,
  MoreHoriz,
  Send,
  Delete,
  Close,
} from "@material-ui/icons";

const useStyles = makeStyles(productStyle);
const useStyle = makeStyles(aboutUsStyle);

/*
|--------------------------------------------------------------------------
| Helper functions and components
|--------------------------------------------------------------------------
*/

const getInitials = (company) => {
  if (!company || typeof company !== "string") {
    return "?";
  }

  const words = company.trim().split(/\s+/).filter(Boolean);

  if (words.length === 0) {
    return "?";
  }

  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase();
  }

  return `${words[0].charAt(0)}${words[1].charAt(0)}`.toUpperCase();
};

const AvatarFallback = ({ company, style }) => {
  return (
    <div
      aria-label={company || "Company avatar"}
      title={company || "Company"}
      style={{
        width: 40,
        height: 40,
        minWidth: 40,
        borderRadius: "50%",
        marginRight: 12,
        border: "1px solid #eeeeee",
        backgroundColor: "#e0e0e0",
        color: "#262626",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 14,
        fontWeight: 700,
        overflow: "hidden",
        ...style,
      }}
    >
      {getInitials(company)}
    </div>
  );
};

const AvatarImage = ({ src, company }) => {
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setHasError(false);
  }, [src]);

  if (!src || hasError) {
    return <AvatarFallback company={company} />;
  }

  return (
    <img
      src={src}
      alt={company || "Company avatar"}
      title={company || "Company"}
      style={{
        width: 40,
        height: 40,
        minWidth: 40,
        borderRadius: "50%",
        marginRight: 12,
        objectFit: "cover",
        border: "1px solid #eeeeee",
      }}
      onError={() => setHasError(true)}
    />
  );
};

/*
|--------------------------------------------------------------------------
| Main component
|--------------------------------------------------------------------------
*/

export default function OfferList() {
  const classes = useStyles();
  const classe = useStyle();
  const history = useHistory();

  const [posts, setPosts] = useState([]);
  const [postErr, setPostErr] = useState("");
  const [ribbonColors, setRibbonColors] = useState({});

  /*
  |--------------------------------------------------------------------------
  | Comment states
  |--------------------------------------------------------------------------
  */

  const [commentModalOpen, setCommentModalOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [commentSaving, setCommentSaving] = useState(false);
  const [commentDeleting, setCommentDeleting] = useState(null);
  const [commentError, setCommentError] = useState("");

  const activeSession = sessionStorage.getItem("sessionData");
  const userObj = JSON.parse(sessionStorage.getItem("sessionData"));

  if (!userObj) {
    history.push("/indian-login");
  }

  const currentUrl = "https://inshabangles.com/offer-list";

  const seoTitle = "Ready Stock Bangles | INDIAN BANGLES";

  const seoDescription =
    "Explore ready stock bangles and imitation jewellery from INDIAN BANGLES. Find elegant designs, multiple sizes, and factory-price products available for immediate order.";

  const seoKeywords =
    "ready stock bangles, bangles in stock, imitation jewellery stock, gold plated bangles, wholesale bangles, INDIAN BANGLES";

  const ogImage =
    "https://indianbangles.in/imgstore/original_logo/IndianBanglesImgLogo.png";

  const stockSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "Ready Stock Bangles",
    url: currentUrl,
    description: seoDescription,
    publisher: {
      "@type": "Organization",
      name: "INDIAN BANGLES",
      logo: {
        "@type": "ImageObject",
        url: ogImage,
      },
    },
  };

  /*
  |--------------------------------------------------------------------------
  | Fetch posts
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    const fetchPosts = async () => {
      // Wait until userObj is available
      if (!userObj?.id) {
        return;
      }

      try {
        const response = await fetch(
          `${localApi}/posts/list/byAdminId/${userObj.admin.id}/byRegisterId/${userObj.id}`,
          {
            method: "GET",
            headers: {
              Authorization: `Basic ${credentials}`,
            },
          },
        );

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(errorText || `HTTP ${response.status}`);
        }

        const json = await response.json();

        const postList = Array.isArray(json)
          ? json
          : Array.isArray(json?.content)
          ? json.content
          : [];

        // console.log("Posts:", postList);

        if (postList.length === 0) {
          history.push("/indian-login");
        } else {
          setPosts(postList);
        }
      } catch (error) {
        console.error("Fetch posts error:", error);
        setPosts([]);
      }
    };

    fetchPosts();
  }, [userObj, localApi, credentials, history]);

  /*
  |--------------------------------------------------------------------------
  | Like / unlike
  |--------------------------------------------------------------------------
  */

  const toggleLike = async (postId) => {
    const post = posts.find((item) => item.id === postId);
    const registerId = userObj?.id;

    if (!post || !registerId) {
      return;
    }

    const oldIsLiked = Boolean(post.isLiked);
    const oldLikes = Number(post.likes) || 0;

    const newIsLiked = !oldIsLiked;
    const newLikes = newIsLiked ? oldLikes + 1 : Math.max(0, oldLikes - 1);

    setPosts((previousPosts) =>
      previousPosts.map((item) =>
        item.id === postId
          ? {
              ...item,
              isLiked: newIsLiked,
              likes: newLikes,
            }
          : item,
      ),
    );

    try {
      const response = await fetch(
        `${localApi}/posts/${postId}/like/${registerId}`,
        {
          method: newIsLiked ? "POST" : "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${credentials}`,
          },
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `HTTP ${response.status}`);
      }

      const result = await response.json();

      setPosts((previousPosts) =>
        previousPosts.map((item) =>
          item.id === postId
            ? {
                ...item,
                isLiked:
                  typeof result.liked === "boolean" ? result.liked : newIsLiked,
                likes:
                  result.likes !== undefined
                    ? Number(result.likes) || 0
                    : newLikes,
              }
            : item,
        ),
      );
    } catch (error) {
      console.error("toggleLike error:", error);

      setPosts((previousPosts) =>
        previousPosts.map((item) =>
          item.id === postId
            ? {
                ...item,
                isLiked: oldIsLiked,
                likes: oldLikes,
              }
            : item,
        ),
      );
    }
  };

  /*
  |--------------------------------------------------------------------------
  | Repost / unrepost
  |--------------------------------------------------------------------------
  */

  const toggleRepost = async (postId) => {
    const post = posts.find((item) => item.id === postId);
    const registerId = userObj?.id;

    if (!post || !registerId) {
      return;
    }

    const oldIsReposted =
      typeof post.isReposted === "boolean"
        ? post.isReposted
        : Boolean(post.isReposts);

    const oldReposts = Number(post.reposts) || 0;

    const newIsReposted = !oldIsReposted;
    const newReposts = newIsReposted
      ? oldReposts + 1
      : Math.max(0, oldReposts - 1);

    setPosts((previousPosts) =>
      previousPosts.map((item) =>
        item.id === postId
          ? {
              ...item,
              isReposted: newIsReposted,
              isReposts: newIsReposted,
              reposts: newReposts,
            }
          : item,
      ),
    );

    try {
      const response = await fetch(
        `${localApi}/posts/${postId}/reposts/${registerId}`,
        {
          method: newIsReposted ? "POST" : "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${credentials}`,
          },
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `HTTP ${response.status}`);
      }

      const result = await response.json();

      const serverIsReposted =
        typeof result.isReposted === "boolean"
          ? result.isReposted
          : typeof result.repost === "boolean"
          ? result.repost
          : typeof result.isReposts === "boolean"
          ? result.isReposts
          : newIsReposted;

      const serverReposts =
        result.reposts !== undefined ? Number(result.reposts) || 0 : newReposts;

      setPosts((previousPosts) =>
        previousPosts.map((item) =>
          item.id === postId
            ? {
                ...item,
                isReposted: serverIsReposted,
                isReposts: serverIsReposted,
                reposts: serverReposts,
              }
            : item,
        ),
      );
    } catch (error) {
      console.error("toggleRepost error:", error);

      setPosts((previousPosts) =>
        previousPosts.map((item) =>
          item.id === postId
            ? {
                ...item,
                isReposted: oldIsReposted,
                isReposts: oldIsReposted,
                reposts: oldReposts,
              }
            : item,
        ),
      );
    }
  };

  /*
  |--------------------------------------------------------------------------
  | Save / unsave
  |--------------------------------------------------------------------------
  */

  const toggleSave = async (postId) => {
    const post = posts.find((item) => item.id === postId);
    const registerId = userObj?.id;

    if (!post || !registerId) {
      return;
    }

    const oldIsSaved = Boolean(post.isSaved);
    const oldSave = Number(post.save) || 0;

    const newIsSaved = !oldIsSaved;
    const newSave = newIsSaved ? oldSave + 1 : Math.max(0, oldSave - 1);

    setPosts((previousPosts) =>
      previousPosts.map((item) =>
        item.id === postId
          ? {
              ...item,
              isSaved: newIsSaved,
              save: newSave,
            }
          : item,
      ),
    );

    try {
      const response = await fetch(
        `${localApi}/posts/${postId}/save/${registerId}`,
        {
          method: newIsSaved ? "POST" : "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${credentials}`,
          },
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `HTTP ${response.status}`);
      }

      const result = await response.json();

      setPosts((previousPosts) =>
        previousPosts.map((item) =>
          item.id === postId
            ? {
                ...item,
                isSaved:
                  typeof result.saved === "boolean" ? result.saved : newIsSaved,
                save:
                  result.save !== undefined
                    ? Number(result.save) || 0
                    : newSave,
              }
            : item,
        ),
      );
    } catch (error) {
      console.error("toggleSave error:", error);

      setPosts((previousPosts) =>
        previousPosts.map((item) =>
          item.id === postId
            ? {
                ...item,
                isSaved: oldIsSaved,
                save: oldSave,
              }
            : item,
        ),
      );
    }
  };

  /*
  |--------------------------------------------------------------------------
  | Comments
  |--------------------------------------------------------------------------
  */

  const openComments = (postId) => {
    const post = posts.find((item) => item.id === postId);

    if (!post) {
      return;
    }

    setSelectedPost(post);
    setCommentText("");
    setCommentError("");
    setCommentModalOpen(true);
  };

  const closeComments = () => {
    if (commentSaving || commentDeleting) {
      return;
    }

    setCommentModalOpen(false);
    setSelectedPost(null);
    setCommentText("");
    setCommentError("");
  };

  /*
  |--------------------------------------------------------------------------
  | Add comment
  |--------------------------------------------------------------------------
  */

  const addComment = async () => {
    const list = localApi + `/register/find/byId/${userObj.id}`;
    const result = await fetch(list, {
      method: "GET",
      headers: {
        Authorization: `Basic ${credentials}`,
      },
    });
    const regData = await result.json();

    const registerId = userObj?.id;
    const adminId = 2;

    if (!selectedPost?.id || !registerId) {
      setCommentError("Please login first.");
      return;
    }

    const trimmedComment = commentText.trim();

    if (!trimmedComment) {
      setCommentError("Please enter a comment.");
      return;
    }

    setCommentSaving(true);
    setCommentError("");

    try {
      const response = await fetch(
        `${localApi}/posts/addComment/${selectedPost.id}/${registerId}/${adminId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${credentials}`,
          },
          body: JSON.stringify({
            comment: trimmedComment,
            userName: userObj.firstname,
            userProfile: regData.profileUrl,
          }),
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `HTTP ${response.status}`);
      }

      const savedComment = await response.json();

      /*
       * Add comment to current post immediately.
       */
      setPosts((previousPosts) =>
        previousPosts.map((item) => {
          if (item.id !== selectedPost.id) {
            return item;
          }

          const oldComments = Array.isArray(item.commentList)
            ? item.commentList
            : [];

          const newComment = {
            ...savedComment,

            /*
             * Fallback values in case backend does not return them.
             */
            comment: savedComment.comment || trimmedComment,
            userName:
              savedComment.userName ||
              userObj?.company ||
              userObj?.firstname ||
              "You",
            userProfile: savedComment.userProfile || regData.profileUrl || "",
          };

          return {
            ...item,
            comments: Number(item.comments) + 1,
            commentList: [newComment, ...oldComments],
          };
        }),
      );

      /*
       * Update selected post as well.
       */
      setSelectedPost((previousPost) => {
        if (!previousPost) {
          return previousPost;
        }

        const oldComments = Array.isArray(previousPost.commentList)
          ? previousPost.commentList
          : [];

        const newComment = {
          ...savedComment,
          comment: savedComment.comment || trimmedComment,
          userName:
            savedComment.userName ||
            userObj?.company ||
            userObj?.firstname ||
            "You",
          userProfile: savedComment.userProfile || regData.profileUrl || "",
        };

        return {
          ...previousPost,
          comments: Number(previousPost.comments) + 1,
          commentList: [newComment, ...oldComments],
        };
      });

      setCommentText("");
    } catch (error) {
      console.error("addComment error:", error);
      setCommentError(error.message || "Failed to add comment.");
    } finally {
      setCommentSaving(false);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | Delete comment
  |--------------------------------------------------------------------------
  */

  const deleteComment = async (commentId) => {
    if (!commentId) {
      return;
    }

    setCommentDeleting(commentId);
    setCommentError("");

    try {
      const response = await fetch(
        `${localApi}/posts/deleteComment/${commentId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Basic ${credentials}`,
          },
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `HTTP ${response.status}`);
      }

      /*
       * Update posts.
       */
      setPosts((previousPosts) =>
        previousPosts.map((item) => {
          if (item.id !== selectedPost?.id) {
            return item;
          }

          const oldComments = Array.isArray(item.commentList)
            ? item.commentList
            : [];

          return {
            ...item,
            comments: Math.max(0, Number(item.comments) - 1),
            commentList: oldComments.filter(
              (comment) => comment.id !== commentId,
            ),
          };
        }),
      );

      /*
       * Update modal.
       */
      setSelectedPost((previousPost) => {
        if (!previousPost) {
          return previousPost;
        }

        const oldComments = Array.isArray(previousPost.commentList)
          ? previousPost.commentList
          : [];

        return {
          ...previousPost,
          comments: Math.max(0, Number(previousPost.comments) - 1),
          commentList: oldComments.filter(
            (comment) => comment.id !== commentId,
          ),
        };
      });
    } catch (error) {
      console.error("deleteComment error:", error);
      setCommentError(error.message || "Failed to delete comment.");
    } finally {
      setCommentDeleting(null);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | Share
  |--------------------------------------------------------------------------
  */

  const openShare = async (post) => {
    const shareUrl = `${window.location.origin}/offer-list#${post.id}`;

    try {
      if (navigator.share) {
        await navigator.share({
          title: "INDIAN BANGLES Offer",
          text: post.description || "",
          url: shareUrl,
        });

        return;
      }

      if (navigator.clipboard) {
        await navigator.clipboard.writeText(shareUrl);
        alert("Offer link copied successfully.");
      }
    } catch (error) {
      console.log("Share was cancelled or unavailable.", error);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | Call
  |--------------------------------------------------------------------------
  */

  const openCall = async (postId) => {
    const post = posts.find((item) => item.id === postId);

    if (!post) {
      return;
    }

    const oldCalls = Number(post.calls) || 0;

    setPosts((previousPosts) =>
      previousPosts.map((item) =>
        item.id === postId
          ? {
              ...item,
              calls: oldCalls + 1,
            }
          : item,
      ),
    );

    try {
      const response = await fetch(
        `${localApi}/posts/updateCallsCount/byId/${postId}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Basic ${credentials}`,
          },
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `HTTP ${response.status}`);
      }
    } catch (error) {
      console.error("openCall error:", error);

      setPosts((previousPosts) =>
        previousPosts.map((item) =>
          item.id === postId
            ? {
                ...item,
                calls: oldCalls,
              }
            : item,
        ),
      );
    }

    window.location.href = "tel:+919967457786";
  };

  /*
  |--------------------------------------------------------------------------
  | WhatsApp
  |--------------------------------------------------------------------------
  */

  const openWhatsApp = async (post) => {
    if (!post) {
      return;
    }

    const postId = post.id;
    const oldWhatsapps = Number(post.whatsapps) || 0;

    setPosts((previousPosts) =>
      previousPosts.map((item) =>
        item.id === postId
          ? {
              ...item,
              whatsapps: oldWhatsapps + 1,
            }
          : item,
      ),
    );

    try {
      const response = await fetch(
        `${localApi}/posts/updateWhatsappsCount/byId/${postId}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Basic ${credentials}`,
          },
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `HTTP ${response.status}`);
      }
    } catch (error) {
      console.error("openWhatsApp error:", error);

      setPosts((previousPosts) =>
        previousPosts.map((item) =>
          item.id === postId
            ? {
                ...item,
                whatsapps: oldWhatsapps,
              }
            : item,
        ),
      );
    }

    const messageText =
      `Hello, I am interested in this offer:\n\n` +
      `Post ID: ${postId}\n` +
      `Description: ${post.description || "N/A"}\n` +
      (post.title ? `Title: ${post.title}\n` : "");

    const text = encodeURIComponent(messageText);

    window.open(
      `https://wa.me/919920246810?text=${text}`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  /*
  |--------------------------------------------------------------------------
  | Image ribbon color
  |--------------------------------------------------------------------------
  */

  const handleImageLoad = (event, postId) => {
    try {
      const image = event.target;

      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");

      if (!context) {
        throw new Error("Canvas context is not available");
      }

      const sampleSize = 50;

      canvas.width = sampleSize;
      canvas.height = sampleSize;

      context.drawImage(image, 0, 0, sampleSize, sampleSize);

      const imageData = context.getImageData(0, 0, sampleSize, sampleSize);

      let redTotal = 0;
      let greenTotal = 0;
      let blueTotal = 0;
      let pixelCount = 0;

      for (let index = 0; index < imageData.data.length; index += 4) {
        const red = imageData.data[index];
        const green = imageData.data[index + 1];
        const blue = imageData.data[index + 2];
        const alpha = imageData.data[index + 3];

        if (alpha < 100) {
          continue;
        }

        if (red > 245 && green > 245 && blue > 245) {
          continue;
        }

        redTotal += red;
        greenTotal += green;
        blueTotal += blue;
        pixelCount += 1;
      }

      if (pixelCount === 0) {
        throw new Error("No usable image pixels");
      }

      const red = Math.round(redTotal / pixelCount);
      const green = Math.round(greenTotal / pixelCount);
      const blue = Math.round(blueTotal / pixelCount);

      setRibbonColors((previous) => ({
        ...previous,
        [postId]: `rgba(${red}, ${green}, ${blue}, 0.88)`,
      }));
    } catch (error) {
      setRibbonColors((previous) => ({
        ...previous,
        [postId]: "rgba(0, 0, 0, 0.55)",
      }));
    }
  };

  /*
  |--------------------------------------------------------------------------
  | Styles
  |--------------------------------------------------------------------------
  */

  const cardStyle = {
    width: "100%",
    height: "auto",
    margin: 0,
    borderRadius: 12,
    overflow: "hidden",
    backgroundColor: "#ffffff",
    boxShadow: "0 4px 14px rgba(0, 0, 0, 0.10)",
    display: "flex",
    flexDirection: "column",
  };

  const imageWrapperStyle = {
    position: "relative",
    height: 260,
    width: "100%",
    overflow: "hidden",
    background: "#f5f5f5",
    flexShrink: 0,
  };

  const imageStyle = {
    display: "block",
    width: "100%",
    height: "100%",
    objectFit: "cover",
  };

  const ribbonTopStyle = {
    position: "absolute",
    top: 12,
    left: "50%",
    transform: "translateX(-50%)",
    color: "#ffffff",
    padding: "6px 10px",
    borderRadius: 5,
    fontSize: 11,
    lineHeight: "14px",
    fontWeight: 700,
    whiteSpace: "nowrap",
    transition: "background 0.3s ease",
    zIndex: 2,
  };

  const ribbonBottomStyle = {
    position: "absolute",
    bottom: 12,
    left: 12,
    background: "rgba(218, 165, 32, 0.93)",
    color: "#111111",
    padding: "6px 14px",
    borderRadius: 5,
    fontSize: 12,
    lineHeight: "14px",
    fontWeight: 700,
    whiteSpace: "nowrap",
    zIndex: 2,
  };

  const ribbonCategoryStyle = {
    position: "absolute",
    top: "50%",
    right: 12,
    transform: "translateY(-50%)",
    background: "rgba(0, 0, 0, 0.55)",
    color: "#ffffff",
    padding: "6px 10px",
    borderRadius: 5,
    fontSize: 12,
    lineHeight: "14px",
    fontWeight: 700,
    textAlign: "center",
    whiteSpace: "nowrap",
    zIndex: 2,
  };

  const headerRowStyle = {
    display: "flex",
    alignItems: "center",
    minHeight: 65,
    padding: "12px 16px",
    background: "#fafafa",
    borderBottom: "1px solid #eeeeee",
    flexShrink: 0,
  };

  const actionsRowStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    minHeight: 52,
    padding: "8px 10px",
    flexShrink: 0,
  };

  const leftActionsStyle = {
    display: "flex",
    alignItems: "center",
    gap: 0,
    overflow: "hidden",
  };

  const actionWithValueStyle = {
    display: "flex",
    alignItems: "center",
    marginRight: 2,
  };

  const actionCountStyle = {
    color: "#444444",
    fontSize: 11,
    fontWeight: 600,
    marginLeft: -5,
    marginRight: 2,
    whiteSpace: "nowrap",
  };

  const captionStyle = {
    color: "#262626",
    fontSize: 13,
    lineHeight: "19px",
  };

  const hashtagStyle = {
    color: "#505050",
    fontSize: 13,
    lineHeight: "18px",
    marginTop: 2,
  };

  const metaRowStyle = {
    display: "flex",
    alignItems: "center",
    marginTop: 3,
  };

  const commentsButtonStyle = {
    padding: 0,
    border: "none",
    background: "transparent",
    color: "#8e8e8e",
    fontSize: 12,
    cursor: "pointer",
  };

  const timeStyle = {
    marginLeft: 8,
    color: "#8e8e8e",
    fontSize: 11,
  };

  /*
  |--------------------------------------------------------------------------
  | Comment modal styles
  |--------------------------------------------------------------------------
  */

  const commentDialogPaperStyle = {
    width: "100%",
    maxWidth: 520,
    borderRadius: 12,
    overflow: "hidden",
  };

  const commentHeaderStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 16px",
    borderBottom: "1px solid #eeeeee",
  };

  const commentListStyle = {
    padding: "8px 0",
    maxHeight: 430,
    overflowY: "auto",
  };

  const commentItemStyle = {
    display: "flex",
    alignItems: "flex-start",
    padding: "10px 16px",
  };

  const commentAvatarStyle = {
    width: 36,
    height: 36,
    minWidth: 36,
    borderRadius: "50%",
    marginRight: 10,
    objectFit: "cover",
    border: "1px solid #eeeeee",
  };

  const commentAvatarFallbackStyle = {
    width: 36,
    height: 36,
    minWidth: 36,
    borderRadius: "50%",
    marginRight: 10,
    background: "#eeeeee",
    color: "#444444",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    fontWeight: 700,
  };

  const commentInputRowStyle = {
    display: "flex",
    alignItems: "center",
    padding: "10px 12px",
    borderTop: "1px solid #eeeeee",
    gap: 8,
  };

  /*
  |--------------------------------------------------------------------------
  | Render
  |--------------------------------------------------------------------------
  */

  return (
    <div className={classes.productPage}>
      <Helmet>
        <html lang="en" />
        <title>{seoTitle}</title>

        <meta name="description" content={seoDescription} />
        <meta name="keywords" content={seoKeywords} />
        <meta name="robots" content="index, follow" />

        <link rel="canonical" href={currentUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="INDIAN BANGLES" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:image" content={ogImage} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content={ogImage} />

        <script type="application/ld+json">
          {JSON.stringify(stockSchema)}
        </script>
      </Helmet>

      <InshaHeader
        brand="INSHA BANGLES PRIVATE LIMITED"
        links={<InshaHeaderLink dropdownHoverColor="dark" />}
        fixed
        color="transparent"
        changeColorOnScroll={{
          height: 100,
          color: "dark",
        }}
      />

      <Parallax
        image={require("assets/img/Product-Stock.jpg").default}
        filter="dark"
        className={classes.pageHeader}
      >
        <div className={classe.container}>
          <GridContainer justify="center">
            <GridItem
              md={8}
              sm={8}
              className={classNames(
                classe.mlAuto,
                classe.mrAuto,
                classe.textCenter,
              )}
            >
              <h1 className={classe.title}>Ready Offer Bangles</h1>

              <h4 style={{ color: "#ffffff" }}>
                Explore our elegant ready stock bangles collection with
                different
                <br />
                designs, sizes, and colors available for immediate order.
              </h4>
            </GridItem>
          </GridContainer>
        </div>
      </Parallax>

      <div
        className={classNames(classes.section, classes.sectionGray)}
        style={{
          paddingTop: 28,
          paddingBottom: 48,
        }}
      >
        <div
          className={classes.container}
          style={{
            paddingLeft: 12,
            paddingRight: 12,
          }}
        >
          <h4 style={{ textAlign: "center" }}>{postErr}</h4>

          <GridContainer
            style={{
              marginTop: 0,
              marginLeft: -12,
              marginRight: -12,
              alignItems: "stretch",
            }}
          >
            {posts.map((post) => {
              const endDate = post.endDate ? new Date(post.endDate) : null;

              const isOfferActive =
                endDate && !Number.isNaN(endDate.getTime())
                  ? endDate.getTime() >= Date.now()
                  : false;

              const isReposted =
                typeof post.isReposted === "boolean"
                  ? post.isReposted
                  : Boolean(post.isReposts);

              return (
                <GridItem
                  key={post.id}
                  xs={12}
                  sm={6}
                  md={4}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    paddingLeft: 12,
                    paddingRight: 12,
                    paddingBottom: 24,
                  }}
                >
                  <Card style={cardStyle}>
                    {/* Header */}
                    <div style={headerRowStyle}>
                      <AvatarImage
                        src={post.avatarUrl}
                        company={post.company}
                      />

                      <div
                        style={{
                          flex: 1,
                          minWidth: 0,
                        }}
                      >
                        <div
                          style={{
                            color: "#262626",
                            fontSize: 14,
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {post.company || "Company"}
                        </div>

                        <div
                          style={{
                            marginTop: 2,
                            color: "#8e8e8e",
                            fontSize: 12,
                          }}
                        >
                          Mumbai, India
                        </div>
                      </div>

                      <Tooltip title="More options">
                        <Button
                          justIcon
                          simple
                          color="transparent"
                          type="button"
                        >
                          <MoreHoriz style={{ color: "#262626" }} />
                        </Button>
                      </Tooltip>
                    </div>

                    {/* Product image */}
                    <div style={imageWrapperStyle}>
                      {post.image ? (
                        <img
                          src={post.image}
                          alt={`${post.company || "Company"} offer`}
                          style={imageStyle}
                          onLoad={(event) => handleImageLoad(event, post.id)}
                          onError={() => {
                            setRibbonColors((previous) => ({
                              ...previous,
                              [post.id]: "rgba(0, 0, 0, 0.55)",
                            }));
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#777777",
                          }}
                        >
                          Image unavailable
                        </div>
                      )}

                      <div
                        style={{
                          ...ribbonTopStyle,
                          background:
                            ribbonColors[post.id] || "rgba(0, 0, 0, 0.55)",
                        }}
                      >
                        Metal ₹ {post.metal || 0} + Making ₹ {post.making || 0}
                      </div>

                      {isOfferActive && post.offer && (
                        <div style={ribbonBottomStyle}>{post.offer}</div>
                      )}

                      <div style={ribbonCategoryStyle}>
                        {post.category || ""}
                        <br />
                        {post.sizemm || ""}
                      </div>
                    </div>

                    {/* Actions */}
                    <div style={actionsRowStyle}>
                      <div style={leftActionsStyle}>
                        {/* Like */}
                        <div style={actionWithValueStyle}>
                          <Tooltip title={post.isLiked ? "Unlike" : "Like"}>
                            <Button
                              justIcon
                              simple
                              color="transparent"
                              type="button"
                              onClick={() => toggleLike(post.id)}
                            >
                              {post.isLiked ? (
                                <Favorite style={{ color: "#ed4956" }} />
                              ) : (
                                <FavoriteBorder style={{ color: "#262626" }} />
                              )}
                            </Button>
                          </Tooltip>

                          <span style={actionCountStyle}>
                            {Number(post.likes) || 0}
                          </span>
                        </div>

                        {/* Comments */}
                        <div style={actionWithValueStyle}>
                          <Tooltip title="Comments">
                            <Button
                              justIcon
                              simple
                              color="transparent"
                              type="button"
                              onClick={() => openComments(post.id)}
                            >
                              <ChatBubbleOutline style={{ color: "#262626" }} />
                            </Button>
                          </Tooltip>

                          <span style={actionCountStyle}>
                            {Number(post.comments) || 0}
                          </span>
                        </div>

                        {/* Repost */}
                        <div style={actionWithValueStyle}>
                          <Tooltip
                            title={isReposted ? "Remove repost" : "Repost"}
                          >
                            <Button
                              justIcon
                              simple
                              color="transparent"
                              type="button"
                              onClick={() => toggleRepost(post.id)}
                            >
                              <Repeat
                                style={{
                                  color: isReposted ? "#1b8f3a" : "#262626",
                                }}
                              />
                            </Button>
                          </Tooltip>

                          <span
                            style={{
                              ...actionCountStyle,
                              color: isReposted ? "#1b8f3a" : "#444444",
                            }}
                          >
                            {Number(post.reposts) || 0}
                          </span>
                        </div>

                        {/* Share */}
                        <Tooltip title="Share">
                          <Button
                            justIcon
                            simple
                            color="transparent"
                            type="button"
                            onClick={() => openShare(post)}
                          >
                            <ShareOutlined
                              style={{
                                color: "#262626",
                                fontSize: 21,
                              }}
                            />
                          </Button>
                        </Tooltip>

                        {/* Calls */}
                        <div style={actionWithValueStyle}>
                          <Tooltip title="Call">
                            <Button
                              justIcon
                              simple
                              color="transparent"
                              type="button"
                              onClick={() => openCall(post.id)}
                            >
                              <Phone style={{ color: "#262626" }} />
                            </Button>
                          </Tooltip>

                          <span style={actionCountStyle}>
                            {Number(post.calls) || 0}
                          </span>
                        </div>

                        {/* WhatsApp */}
                        <div style={actionWithValueStyle}>
                          <Tooltip title="WhatsApp">
                            <Button
                              justIcon
                              simple
                              color="transparent"
                              type="button"
                              onClick={() => openWhatsApp(post)}
                            >
                              <WhatsApp style={{ color: "#25D366" }} />
                            </Button>
                          </Tooltip>

                          <span style={actionCountStyle}>
                            {Number(post.whatsapps) || 0}
                          </span>
                        </div>
                      </div>

                      {/* Save */}
                      <Tooltip title={post.isSaved ? "Unsave" : "Save"}>
                        <Button
                          justIcon
                          simple
                          color="transparent"
                          type="button"
                          onClick={() => toggleSave(post.id)}
                        >
                          {post.isSaved ? (
                            <Bookmark style={{ color: "#262626" }} />
                          ) : (
                            <BookmarkBorder style={{ color: "#262626" }} />
                          )}
                        </Button>
                      </Tooltip>
                    </div>

                    {/* Caption */}
                    <CardBody
                      style={{
                        paddingTop: 2,
                        paddingRight: 16,
                        paddingBottom: 0,
                        paddingLeft: 16,
                      }}
                    >
                      <div style={captionStyle}>
                        <span
                          style={{
                            fontWeight: 600,
                            marginRight: 5,
                          }}
                        >
                          {post.company || "Company"}
                        </span>

                        {post.description || ""}
                      </div>

                      <span>
                        {post.category || ""}
                        {post.category || post.sizemm ? ", " : ""}
                        {post.sizemm || ""}
                        {post.metalGram !== undefined
                          ? `, Metal Gram ${post.metalGram}`
                          : ""}
                        {post.readyGram !== undefined
                          ? `, Ready Gram ${post.readyGram}`
                          : ""}
                      </span>

                      {post.hashtag && (
                        <div style={hashtagStyle}>{post.hashtag}</div>
                      )}

                      <div style={metaRowStyle}>
                        <button
                          type="button"
                          onClick={() => openComments(post.id)}
                          style={commentsButtonStyle}
                        >
                          View all {Number(post.comments) || 0} comments
                        </button>

                        {post.timeAgo && (
                          <span style={timeStyle}>{post.timeAgo}</span>
                        )}
                      </div>
                    </CardBody>
                  </Card>
                </GridItem>
              );
            })}
          </GridContainer>
        </div>
      </div>

      {/*
      |--------------------------------------------------------------------------
      | Comments Dialog
      |--------------------------------------------------------------------------
      */}

      <Dialog
        open={commentModalOpen}
        onClose={closeComments}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          style: commentDialogPaperStyle,
        }}
      >
        {/* Comment header */}
        <DialogTitle disableTypography style={commentHeaderStyle}>
          <div
            style={{
              fontSize: 17,
              fontWeight: 600,
              color: "#262626",
            }}
          >
            Comments
            {selectedPost && (
              <span
                style={{
                  marginLeft: 6,
                  color: "#8e8e8e",
                  fontSize: 13,
                  fontWeight: 400,
                }}
              >
                {Number(selectedPost.comments) || 0}
              </span>
            )}
          </div>

          <IconButton
            size="small"
            onClick={closeComments}
            disabled={commentSaving || commentDeleting}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent
          style={{
            padding: 0,
          }}
        >
          {/* Comments list */}
          <div style={commentListStyle}>
            {selectedPost &&
            Array.isArray(selectedPost.commentList) &&
            selectedPost.commentList.length > 0 ? (
              selectedPost.commentList.map((comment) => {
                const commentUserName =
                  comment.userName || comment.company || "";

                const commentProfile =
                  comment.userProfile || comment.avatarUrl || "";

                const commentTextValue = comment.comment || "";

                return (
                  <div key={comment.id} style={commentItemStyle}>
                    {commentProfile ? (
                      <img
                        src={commentProfile}
                        alt={commentUserName}
                        style={commentAvatarStyle}
                        onError={(event) => {
                          event.currentTarget.style.display = "none";
                          if (event.currentTarget.nextSibling) {
                            event.currentTarget.nextSibling.style.display =
                              "flex";
                          }
                        }}
                      />
                    ) : null}

                    <div
                      style={{
                        ...commentAvatarFallbackStyle,
                        display: commentProfile ? "none" : "flex",
                      }}
                    >
                      {getInitials(commentUserName)}
                    </div>

                    <div
                      style={{
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <span
                          style={{
                            color: "#262626",
                            fontSize: 13,
                            fontWeight: 600,
                          }}
                        >
                          {commentUserName}
                        </span>

                        <IconButton
                          size="small"
                          onClick={() => deleteComment(comment.id)}
                          disabled={commentDeleting === comment.id}
                          style={{
                            padding: 4,
                          }}
                        >
                          {commentDeleting === comment.id ? (
                            <CircularProgress size={16} />
                          ) : (
                            <Delete
                              style={{
                                fontSize: 18,
                                color: "#999999",
                              }}
                            />
                          )}
                        </IconButton>
                      </div>

                      <div
                        style={{
                          marginTop: 3,
                          color: "#262626",
                          fontSize: 13,
                          lineHeight: "19px",
                          wordBreak: "break-word",
                        }}
                      >
                        {commentTextValue}
                      </div>

                      {comment.createdAt && (
                        <div
                          style={{
                            marginTop: 3,
                            color: "#999999",
                            fontSize: 10,
                          }}
                        >
                          {new Date(comment.createdAt).toLocaleString()}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <div
                style={{
                  padding: "45px 20px",
                  textAlign: "center",
                  color: "#999999",
                  fontSize: 13,
                }}
              >
                No comments yet.
                <br />
                Be the first to comment.
              </div>
            )}
          </div>

          {/* Error */}
          {commentError && (
            <div
              style={{
                padding: "4px 16px 8px",
                color: "#d32f2f",
                fontSize: 12,
              }}
            >
              {commentError}
            </div>
          )}
        </DialogContent>

        {/* Add comment */}
        <DialogActions
          style={{
            padding: 0,
            display: "block",
          }}
        >
          <div style={commentInputRowStyle}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Add a comment..."
              value={commentText}
              onChange={(event) => {
                setCommentText(event.target.value);

                if (commentError) {
                  setCommentError("");
                }
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();

                  if (!commentSaving) {
                    addComment();
                  }
                }
              }}
              disabled={commentSaving}
              inputProps={{
                maxLength: 1000,
              }}
            />

            <IconButton
              color="primary"
              onClick={addComment}
              disabled={commentSaving || !commentText.trim() || !userObj?.id}
            >
              {commentSaving ? <CircularProgress size={22} /> : <Send />}
            </IconButton>
          </div>
        </DialogActions>
      </Dialog>

      <Footer />
    </div>
  );
}
